export interface ProjectItem {
  slug?: string;
  title: string;
  description: string;
  shortDescription?: string;
  technologies: string[];
  period: string;
  image: string;
  github?: string;
  commitRepo?: string;
  demo?: string;
  liveSite?: string;
  relatedProjects?: { title: string; github?: string; description?: string }[];
  hideFromHome?: boolean;
  workType?: "Experiment" | "Project";
  /** Title of a writeup hosted elsewhere, shown in place of `title` on /work. */
  externalWriteupTitle?: string;
}

export const projects: ProjectItem[] = [
  {
    slug: "stateless-desktops",
    title: "Stateless Desktop Sandboxes",
    period: "Jul. 2026",
    shortDescription: "Desktop sandboxes that park between agent turns.",
    description: "A runtime and control plane for full Linux desktop sandboxes that behave like serverless objects: stateful to the agent, disposable to the infrastructure. Each desktop runs in a Firecracker microVM and parks between agent turns — snapshotting memory and shutting the VM down when idle, then reconstructing it in under a second for the next action. Built around incremental memory snapshots, copy-on-write disk, and checkpoint lineage, with a fleet control plane for spawning, streaming, and parking many desktops per host.\n\nRead more in my blog: [I tried to make stateful desktops stateless](/stateless).",
    technologies: ["Firecracker", "KVM", "Node.js", "TypeScript", "AWS", "noVNC"],
    image: "stateless-demo.png",
    workType: "Experiment"
  },
  {
    slug: "baselayer",
    title: "BaseLayer",
    period: "May 2026",
    shortDescription: "Fast browser infrastructure running Chromium in Firecracker microVMs.",
    description: "BaseLayer is an open-source browser hosting control plane and host runtime for running isolated Chromium sessions with Docker and Firecracker-backed microVMs.",
    technologies: ["Firecracker", "Chromium", "TypeScript", "AWS"],
    liveSite: "https://baselayer.sh",
    github: "https://github.com/Lasdw6/BaseLayer",
    image: "baselayer-hero.png",
    externalWriteupTitle: "How I built the fastest browser infrastructure"
  },
  {
    slug: "piper",
    title: "Piper",
    period: "Apr. 2026",
    shortDescription: "Continual context compression for long-running coding agents.",
    description: "Piper is a research codebase for compressing the working context of long-horizon coding agents. Its conservative thresholded-hybrid policy keeps recent tool context intact, compacts older tool output only under pressure, and benchmarks correctness and token use against an uncompressed baseline.",
    technologies: ["TypeScript", "Python", "LiteLLM", "SWE-bench"],
    github: "https://github.com/Lasdw6/Piper",
    image: "piper-tile.png",
    workType: "Experiment"
  },
  {
    slug: "boostos-ai",
    title: "BoostOS.ai",
    period: "Mar. 2026",
    shortDescription: "Operating system for human-AI coding.",
    description: "BoostOS helps you orchestrate your AI agents, dev servers, and IDEs from one desktop control plane.",
    technologies: ["AI Systems", "Automation", "Product Engineering"],
    liveSite: "https://boostos.ai",
    github: "https://github.com/Lasdw6/Boost",
    image: "boostos-hero.png",
    workType: "Experiment"
  },
  {
    slug: "orbit",
    title: "Orbit",
    period: "Mar. 2026",
    shortDescription: "A private personal CRM for remembering people, conversations, and follow-ups.",
    description: "Orbit is a local-first personal CRM for keeping track of people, conversations, and thoughtful follow-ups without turning relationships into busywork. The mobile app is built with Expo and React Native, with an Astro site for its public presence.",
    technologies: ["Expo", "React Native", "SQLite", "Astro", "TypeScript"],
    liveSite: "https://orbit-five-smoky.vercel.app",
    github: "https://github.com/Lasdw6/Orbit",
    image: "https://raw.githubusercontent.com/Lasdw6/Orbit/HEAD/apps/mobile/store-assets/google-play/feature-graphic-1024x500.png"
  },
  {
    slug: "harnessgg",
    title: "Harness.gg",
    period: "Jan. 2026",
    shortDescription: "CLI tools that let AI agents control desktop apps.",
    description: "Harness.gg is a collection of open-source CLI tools that let AI agents control desktop apps. No MCP. No setup. Just install and go.",
    technologies: ["CLI Tools", "Astro", "TypeScript", "React", "Tailwind CSS"],
    liveSite: "https://harness.gg",
    github: "https://github.com/Lasdw6/harnessgg",
    image: "harness-hero.png"
  },
  {
    title: "Agentic Personal Assistant",
    period: "Jan. 2025 - Present",
    shortDescription: "Multi-agentic system for email and calendar management with distributed architecture.",
    description: "Multi-agentic system (APRIL) using MCP servers for email and calendar management. Features distributed architecture with FastAPI, Redis, Celery, and specialized agents. Implements role-based safety model separating read/write operations, RAG for document retrieval, and real-time status streaming via SSE.\n\nRead more in my blog: [My Favorite Project](/favorite-project).",
    technologies: ["Python", "FastAPI", "Langchain", "Redis", "Celery", "MCP", "Pinecone", "Docker"],
    github: "https://github.com/Lasdw6/agentic-personal-assistant",
    commitRepo: "https://github.com/Lasdw6/Assistant",
    image: "apa-tile.png",
    workType: "Experiment",
    relatedProjects: [
      {
        title: "Assistant App",
        github: "https://github.com/Lasdw6/AssistantApp",
        description: "React Native mobile application for the Personal Assistant system"
      }
    ]
  },
  {
    title: "Tea Tree Chat",
    period: "June 2025 - Present",
    shortDescription: "BYOK chat application with multiple AI model support via OpenRouter.",
    description: "Modern BYOK (Bring Your Own Key) chat application built as a T3 Chat clone for the Cloneathon. Features real-time streaming responses, multiple AI model support via OpenRouter (Meta, Anthropic, OpenAI, Google, Mistral), chat forking and regeneration, encrypted API key storage, and JWT authentication. Built with Next.js 14 frontend and FastAPI backend with SQLite/Postgres, deployed on Vercel and Render with server wake-up functionality.",
    technologies: ["Python", "FastAPI", "Next.js", "Typescript", "Postgres", "OpenRouter", "JWT"],
    liveSite: "https://askteatree.chat",
    github: "https://github.com/Lasdw6/TeaTreeChat",
    image: "Teatree.png"
  },
  {
    title: "Job Application Tracker",
    period: "June 2024 - Sep. 2024",
    shortDescription: "Web app for tracking job applications with LLM email parsing.",
    description: "Web app for tracking job applications, with LLM email parsing and cloud deployment.",
    technologies: ["Python", "Typescript", "React", "MongoDB", "OAuth2", "LLMs", "Git"],
    github: "https://github.com/Lasdw6",
    image: "JobTracker.png",
    hideFromHome: true
  },
  {
    title: "Desktop Agent",
    period: "2024 - Present",
    shortDescription: "Cross-platform desktop automation assistant with computer vision and OCR.",
    description: "Cross-platform desktop automation assistant built with Tauri (Rust + React) and Python Flask. Analyzes live desktop screens with computer vision and OCR to discover actionable UI elements. Executes element-aware actions with coordinate fallbacks, includes specialized browser automation for Windows via UI Automation, and integrates GPT for vision-based task completion. Features real-time UI element discovery, multi-source context for LLMs, and action execution with logging.",
    technologies: ["Rust", "Tauri", "Python", "Flask", "React", "TypeScript", "OpenCV", "OpenAI"],
    github: "https://github.com/Lasdw6/Desktop_Agent",
    image: "desktop-agent-discovery.png",
    workType: "Experiment"
  },
  {
    title: "Housing Search Agent",
    period: "2024 - Present",
    shortDescription: "AI agent for automated housing search using browser automation.",
    description: "Full-stack web application for University of Waterloo students to find housing listings. Features Next.js frontend with structured search filters (price, location, gender, term, furnished) and FastAPI backend that performs real-time scraping across Kijiji, Reddit, and Facebook. Uses asynchronous scraping with timeout fallbacks, smart filtering, and scoring system to return top 10 relevant listings. Deployed on Vercel (frontend) and Railway/Render (backend).",
    technologies: ["Python", "FastAPI", "Next.js", "TypeScript", "Playwright", "Browser Automation"],
    github: "https://github.com/Lasdw6/HousingSearchAgent",
    image: "housing-search.png"
  },
  {
    title: "Discoveriffy",
    period: "2024",
    shortDescription: "Flask web app that analyzes Spotify playlists and provides music recommendations.",
    description: "Flask web application that analyzes Spotify playlists using audio features (danceability, energy, valence, tempo) and provides personalized music recommendations. Features secure OAuth integration, playlist analysis, and smart recommendations based on audio characteristics.",
    technologies: ["Python", "Flask", "Spotify API", "Music Analysis", "OAuth"],
    github: "https://github.com/Lasdw6/Discoveriffy",
    image: "image.png",
    hideFromHome: true
  },
  {
    title: "Deep Research Agent",
    period: "Apr. 2025 - May 2025",
    shortDescription: "AI-powered research assistant for web search, image analysis, and multi-modal tasks.",
    description: "AI-powered research assistant (ScholarAI) that helps find answers by searching the web, analyzing images, processing audio, and handling code/data files. Built with LangChain and LangGraph, deployed on Hugging Face Spaces with Gradio interface. Features web search and Wikipedia integration, image analysis, audio transcription with Whisper, code analysis, and data file processing (PDF, Excel, etc.). Tested on the GAIA benchmark.",
    technologies: ["Python", "Langgraph", "Langchain", "Gradio", "OpenAI", "Whisper"],
    liveSite: "https://huggingface.co/spaces/Lasdw/Deep_Research_Agent",
    github: "https://github.com/Lasdw6/Deep_Research_Assisntant",
    image: "Deep_Research_Agent.png",
    workType: "Experiment"
  },
  {
    title: "Personal Portfolio Website",
    period: "Mar. 2024 - Present",
    shortDescription: "Minimalistic personal portfolio website with project showcases and blog.",
    description: "Minimalistic personal portfolio website built with React and TypeScript. Features a clean, single-page design with project showcases, work experience, blog posts, and GitHub integration. Uses Tailwind CSS for styling with a dark navy and blue accent color scheme.",
    technologies: ["React", "TypeScript", "Tailwind CSS", "React Router"],
    github: "https://github.com/Lasdw6/Website",
    liveSite: "https://vividh.lol",
    image: "image.png",
    hideFromHome: true
  },
  {
    title: "Chess Game with Computer Players",
    period: "June 2024 - Aug. 2024",
    shortDescription: "Chess game with computer AI and OOP design.",
    description: "Command-line chess game built in C++ with multiple AI difficulty levels. Supports human vs. human, human vs. AI, and AI vs. AI matches. Features include castling, en passant, pawn promotion, setup mode for custom board configurations, and AI with 4+ difficulty levels ranging from random moves to advanced strategies. Built with OOP principles and std::vector for board representation.",
    technologies: ["C++", "std::vector", "OOP"],
    github: "https://github.com/Lasdw6/chess.exe",
    image: "Chess.jpg",
    hideFromHome: true
  }
];
